<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';
import { formatCurrency } from '../helpers/orderHelpers';

const route = useRoute();
const router = useRouter();

const openCampaign = campaign => {
  router.push({
    name: 'meta_ads_detail',
    params: { accountId: route.params.accountId, id: campaign.id },
  });
};

const ACCOUNT_LABELS = {
  koza: { label: 'Koza Butik Reklamları', dot: 'bg-n-amber-9' },
  modax: { label: 'ModaXlarge.com Reklam Hesabı', dot: 'bg-n-teal-9' },
  modax_shopify: { label: 'ModaXLarge (Shopify)', dot: 'bg-n-iris-9' },
};

// Karne kuralı (4 Ağu denetimi): mesaj kampanyası TL/mesaj ile, satış
// kampanyası TL/satış ile ölçülür — iki tür aynı cetvelle KIYASLANMAZ.
// Eşikler denetim raporundaki çıtalar; düşük harcamada hüküm verilmez.
const karne = campaign => {
  if (campaign.isSalesObjective) {
    if (campaign.totalSpend < 500 || campaign.costPerPurchase == null)
      return null;
    if (campaign.costPerPurchase <= 300)
      return {
        etiket: `⭐ ${Math.round(campaign.costPerPurchase)} TL/satış`,
        renk: 'bg-n-teal-3 text-n-teal-11',
      };
    if (campaign.costPerPurchase <= 500)
      return {
        etiket: `🟡 ${Math.round(campaign.costPerPurchase)} TL/satış`,
        renk: 'bg-n-amber-3 text-n-amber-11',
      };
    return {
      etiket: `❌ ${Math.round(campaign.costPerPurchase)} TL/satış`,
      renk: 'bg-n-ruby-3 text-n-ruby-11',
    };
  }
  if (campaign.totalSpend < 300 || campaign.costPerMessage == null) return null;
  if (campaign.costPerMessage <= 10)
    return {
      etiket: `⭐ ${campaign.costPerMessage.toFixed(1)} TL/mesaj`,
      renk: 'bg-n-teal-3 text-n-teal-11',
    };
  if (campaign.costPerMessage <= 15)
    return {
      etiket: `🟡 ${campaign.costPerMessage.toFixed(1)} TL/mesaj`,
      renk: 'bg-n-amber-3 text-n-amber-11',
    };
  return {
    etiket: `❌ ${campaign.costPerMessage.toFixed(1)} TL/mesaj`,
    renk: 'bg-n-ruby-3 text-n-ruby-11',
  };
};

const isLoading = ref(true);
const loadError = ref('');
const campaigns = ref([]);
const accountFilter = ref(
  ['koza', 'modax', 'modax_shopify'].includes(route.query.hesap)
    ? route.query.hesap
    : 'all'
);
const statusFilter = ref('active');

const loadCampaigns = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const data = await fetchMarketplaceJson(route.params.accountId, 'meta_ads');
    campaigns.value = data.campaigns || [];
  } catch {
    loadError.value = 'Reklam verisi yüklenemedi.';
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadCampaigns);

// Sol menüden (sidebar) hesap alt menüleri ?hesap= query parametresini
// degistirir ama ayni route oldugu icin sayfa yeniden kurulmaz — bu yuzden
// query degisimini izleyip filtreyi guncelliyoruz (yoksa "Tümü" disindakiler
// calismiyor gibi gorunuyordu).
watch(
  () => route.query.hesap,
  hesap => {
    accountFilter.value = ['koza', 'modax', 'modax_shopify'].includes(hesap)
      ? hesap
      : 'all';
  }
);

const accountTabs = [
  { key: 'all', label: 'Tümü' },
  { key: 'koza', label: ACCOUNT_LABELS.koza.label },
  { key: 'modax', label: ACCOUNT_LABELS.modax.label },
  { key: 'modax_shopify', label: ACCOUNT_LABELS.modax_shopify.label },
];

const statusTabs = [
  { key: 'active', label: 'Aktif' },
  { key: 'paused', label: 'Pasif' },
  { key: 'all', label: 'Tümü' },
];

const byAccount = computed(() =>
  accountFilter.value === 'all'
    ? campaigns.value
    : campaigns.value.filter(c => c.accountKey === accountFilter.value)
);

const filteredCampaigns = computed(() => {
  let list = byAccount.value;
  if (statusFilter.value === 'active') {
    list = list.filter(c => c.effectiveStatus === 'ACTIVE');
  } else if (statusFilter.value === 'paused') {
    list = list.filter(c => c.effectiveStatus !== 'ACTIVE');
  }
  // En son baslayan/olusturulan kampanya en ustte.
  return [...list].sort(
    (a, b) => new Date(b.campaignCreatedAt) - new Date(a.campaignCreatedAt)
  );
});

// Sekme basliklarinda durum sayisini gostermek icin (hesap filtresine gore).
const statusCounts = computed(() => ({
  active: byAccount.value.filter(c => c.effectiveStatus === 'ACTIVE').length,
  paused: byAccount.value.filter(c => c.effectiveStatus !== 'ACTIVE').length,
  all: byAccount.value.length,
}));

const summary = computed(() => {
  const list = filteredCampaigns.value;
  return {
    totalSpend: list.reduce((sum, c) => sum + Number(c.totalSpend || 0), 0),
    totalClicks: list.reduce((sum, c) => sum + Number(c.totalClicks || 0), 0),
    totalMessages: list.reduce(
      (sum, c) => sum + Number(c.messagingConversations || 0),
      0
    ),
    matchedCount: list.filter(c => c.matchedProductCode).length,
    totalCount: list.length,
  };
});

const formatDate = iso => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = n => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const dateRangeLabel = campaign => {
  const start = formatDate(campaign.startTime || campaign.campaignCreatedAt);
  if (campaign.effectiveStatus === 'ACTIVE') return `${start} → şu an`;
  const stop = formatDate(campaign.stopTime);
  return campaign.stopTime ? `${start} → ${stop}` : start;
};

const truncate = (text, max = 90) => {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-4 px-4 py-4 border-b sm:px-6 border-n-weak flex-shrink-0"
    >
      <div class="min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">
          Meta Reklam Kampanyaları
        </h1>
        <p class="text-sm truncate text-n-slate-11">
          Gerçek kampanya adı, tarihi ve hedefine göre harcama/dönüşüm verisi
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 transition-colors flex-shrink-0"
        :disabled="isLoading"
        @click="loadCampaigns"
      >
        <span
          class="size-4 i-lucide-refresh-cw"
          :class="{ 'animate-spin': isLoading }"
        />
        Yenile
      </button>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">Kampanyalar yükleniyor…</p>
      </div>

      <div
        v-else-if="loadError"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <span class="size-9 i-lucide-alert-triangle text-n-ruby-9" />
        <p class="text-sm">{{ loadError }}</p>
      </div>

      <div v-else class="px-4 py-5 mx-auto max-w-7xl sm:px-6">
        <!-- Hesap filtreleri -->
        <div class="flex flex-wrap gap-2 mb-3">
          <button
            v-for="tab in accountTabs"
            :key="tab.key"
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
            :class="
              accountFilter === tab.key
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="accountFilter = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Durum filtreleri -->
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            v-for="tab in statusTabs"
            :key="tab.key"
            type="button"
            class="px-3 py-1 text-xs font-medium rounded-full transition-colors"
            :class="
              statusFilter === tab.key
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-solid-2 text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="statusFilter = tab.key"
          >
            {{ tab.label }} ({{ statusCounts[tab.key] }})
          </button>
        </div>

        <!-- Ozet kartlari -->
        <div class="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">Toplam Harcama</p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatCurrency(summary.totalSpend) }}
            </p>
          </div>
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">Toplam Tıklama</p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ summary.totalClicks }}
            </p>
          </div>
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">Mesajlaşma Dönüşümü</p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ summary.totalMessages }}
            </p>
          </div>
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">Ürünle Eşleşen Kampanya</p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ summary.matchedCount }} / {{ summary.totalCount }}
            </p>
          </div>
        </div>

        <!-- Reklam-satis kopuklugu uyarisi -->
        <div
          class="flex items-start gap-2 p-3 mb-5 text-sm rounded-xl bg-n-amber-2 text-n-amber-11 outline outline-1 outline-n-amber-6"
        >
          <span class="flex-shrink-0 mt-0.5 size-4 i-lucide-info" />
          <p>
            ModaXLarge reklamlarından gelen mesajlaşma siparişlerinin bir kısmı
            (eski Dialogtab sürecinden kalan alışkanlıkla) Shopify'a değil
            Ticimax'a giriliyor olabilir. Buradaki "satış adedi" Ticimax +
            Shopify toplamını gösterir, bu yüzden ROI hesabı kanal bazında değil
            ürün bazında daha güvenilirdir.
          </p>
        </div>

        <!-- Kampanya listesi -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            class="flex flex-col overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak cursor-pointer hover:outline-n-brand transition-colors"
            @click="openCampaign(campaign)"
          >
            <div class="flex gap-3 p-3">
              <img
                v-if="campaign.thumbnailUrl"
                :src="campaign.thumbnailUrl"
                class="object-cover rounded-lg size-16 flex-shrink-0"
                alt=""
              />
              <div
                v-else
                class="flex items-center justify-center rounded-lg size-16 flex-shrink-0 bg-n-solid-3"
              >
                <span class="size-6 i-lucide-image text-n-slate-9" />
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-1.5 mb-1">
                  <span
                    class="rounded-full size-1.5"
                    :class="ACCOUNT_LABELS[campaign.accountKey]?.dot"
                  />
                  <span class="text-[11px] text-n-slate-10 truncate">
                    {{
                      ACCOUNT_LABELS[campaign.accountKey]?.label ||
                      campaign.accountKey
                    }}
                  </span>
                  <span
                    class="px-1.5 py-0.5 text-[10px] font-medium rounded-full flex-shrink-0"
                    :class="
                      campaign.effectiveStatus === 'ACTIVE'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-slate-3 text-n-slate-10'
                    "
                  >
                    {{
                      campaign.effectiveStatus === 'ACTIVE' ? 'Aktif' : 'Pasif'
                    }}
                  </span>
                  <span
                    v-if="karne(campaign)"
                    class="px-1.5 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0"
                    :class="karne(campaign).renk"
                    title="Karne: mesaj kampanyası TL/mesaj, satış kampanyası TL/satış ile ölçülür"
                  >
                    {{ karne(campaign).etiket }}
                  </span>
                </div>
                <p
                  class="text-sm font-medium truncate text-n-slate-12"
                  :title="campaign.name"
                >
                  {{ campaign.name }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ dateRangeLabel(campaign) }}
                </p>
              </div>
            </div>

            <p
              class="px-3 text-xs text-n-slate-11 line-clamp-2"
              :title="campaign.creativeBody"
            >
              {{ truncate(campaign.creativeBody) }}
            </p>

            <div class="px-3 py-2 mt-2">
              <div
                v-if="campaign.product"
                class="flex items-center gap-2 p-2 rounded-lg bg-n-solid-1"
              >
                <img
                  v-if="campaign.product.image"
                  :src="campaign.product.image"
                  class="object-cover rounded size-8 flex-shrink-0"
                  alt=""
                />
                <div class="min-w-0">
                  <p
                    class="text-xs font-medium truncate text-n-slate-12"
                    :title="campaign.product.title"
                  >
                    {{ campaign.product.title }}
                  </p>
                  <p class="text-[11px] text-n-slate-10">
                    {{ campaign.product.code }}
                  </p>
                </div>
              </div>
              <p v-else class="text-xs text-n-slate-9">
                Ürün otomatik eşleştirilemedi (genel/katalog reklamı)
              </p>
            </div>

            <!-- Hedefe gore metrik: mesaj donusumu hedefli kampanyada "Mesaj"
                 satis hedefli kampanyada gosterilmez, cunku o kampanya icin
                 anlamsiz (optimize edilen metrik degil). -->
            <div
              class="grid px-3 py-2 mt-auto border-t border-n-weak"
              :class="campaign.isSalesObjective ? 'grid-cols-3' : 'grid-cols-4'"
            >
              <div>
                <p class="text-[10px] text-n-slate-10">Harcama</p>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ formatCurrency(campaign.totalSpend) }}
                </p>
              </div>
              <div v-if="!campaign.isSalesObjective">
                <p class="text-[10px] text-n-slate-10">Mesaj</p>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ campaign.messagingConversations }}
                </p>
              </div>
              <div v-if="!campaign.isSalesObjective">
                <p class="text-[10px] text-n-slate-10">TL/Mesaj</p>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{
                    campaign.costPerMessage != null
                      ? campaign.costPerMessage.toFixed(1)
                      : '—'
                  }}
                </p>
              </div>
              <div v-if="campaign.isSalesObjective">
                <p
                  class="text-[10px] text-n-slate-10"
                  title="Meta piksel satışına göre — satış kampanyasının asıl karnesi"
                >
                  TL/Satış
                </p>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{
                    campaign.costPerPurchase != null
                      ? Math.round(campaign.costPerPurchase)
                      : '—'
                  }}
                </p>
              </div>
              <div>
                <p class="text-[10px] text-n-slate-10">Satış Adedi</p>
                <p
                  class="text-sm font-semibold"
                  :class="
                    campaign.unitsSold ? 'text-n-teal-11' : 'text-n-slate-9'
                  "
                >
                  {{ campaign.unitsSold != null ? campaign.unitsSold : '—' }}
                </p>
                <!-- Meta'nin kendi piksel/CAPI verisiyle karsilastirma —
                     genelde bizim sayimizdan DUSUK cikar: piksel kapida
                     odeme/cerez engelleyici gibi sebeplerle gercek
                     satislarin bir kismini goremiyor, Ticimax siparislerini
                     ise hic gormez (ayri site/piksel). -->
                <p
                  v-if="campaign.metaPurchases"
                  class="text-[10px] text-n-slate-9"
                  title="Meta'nın kendi piksel/CAPI verisine göre satın alma sayısı — genelde gerçek satıştan düşük çıkar"
                >
                  Meta: {{ campaign.metaPurchases }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="!filteredCampaigns.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          Bu filtrede kampanya bulunamadı.
        </p>
      </div>
    </div>
  </div>
</template>
